import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, extractError } from '@/lib/api.js';
import { PageLayout } from '@/components/layout/PageLayout.js';
import { DiagnosticoFlow } from '@/features/cinco-forcas/DiagnosticoFlow.js';

/**
 * O sócio responde o autodiagnóstico do radar: as 50 perguntas do diagnóstico
 * 5 Forças, sozinho. As respostas são salvas a cada tema; dá para parar e continuar depois.
 */

type MinhaResposta = {
  radar_id: string;
  tipo: 'PARTIDA' | 'MARCO' | 'FINAL';
  aberto: boolean;
  empresa: { id: string; nome: string };
  diagnostico_id: string;
  respostas: (number | null)[] | null;
  concluida: boolean;
};

const NOME = { PARTIDA: 'Radar de partida', MARCO: 'Radar de marco', FINAL: 'Radar final' } as const;

export function ConselheiroResponderPage() {
  const { radarId = '' } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['conselheiro-minha-resposta', radarId],
    queryFn: async () => (await api.get<MinhaResposta>(`/conselheiro/radares/${radarId}/minha-resposta`)).data,
    // As respostas iniciais não podem mudar no meio do questionário.
    staleTime: Infinity,
  });

  if (q.isLoading) return <PageLayout title="Autodiagnóstico">Carregando…</PageLayout>;
  if (q.isError || !q.data) {
    return (
      <PageLayout title="Autodiagnóstico">
        <p className="text-sm text-red-600">{extractError(q.error).message}</p>
      </PageLayout>
    );
  }
  const r = q.data;
  const voltar = () => {
    void qc.invalidateQueries({ queryKey: ['conselheiro-painel'] });
    nav('/direcao/conselheiro');
  };

  if (!r.aberto || r.concluida) {
    return (
      <PageLayout title={`${NOME[r.tipo]} — ${r.empresa.nome}`}>
        <p className="text-sm text-ha-subtle">
          {r.concluida
            ? 'Você já concluiu este autodiagnóstico. O conselheiro fecha o radar com as notas finais.'
            : 'Este radar já foi fechado pelo conselheiro.'}
        </p>
        <button type="button" className="mt-4 text-sm text-accent underline" onClick={voltar}>
          Voltar ao Conselheiro
        </button>
      </PageLayout>
    );
  }

  return (
    <div>
      <p className="mx-auto mb-4 max-w-3xl text-sm text-ha-subtle">
        <strong className="text-ha-text">
          {NOME[r.tipo]} — {r.empresa.nome}.
        </strong>{' '}
        Responda pensando na empresa como ela está hoje, sozinho — cada sócio responde o seu. Suas respostas são salvas a
        cada tema (de cinco em cinco perguntas).
      </p>
      <DiagnosticoFlow
        diagnosticoId={r.diagnostico_id}
        initialAnswers={r.respostas ?? undefined}
        onCancel={voltar}
        onComplete={voltar}
      />
    </div>
  );
}
